/* ============================================================================
   ahcut design system — class recipes (shared by Astro + React)
   ----------------------------------------------------------------------------
   Dependency-free Tailwind class strings for the primitives both apps render.
   Astro components (web/src/components/ui/*) and React components
   (dashboard/src/components/ui/*) MUST build their classes from here so a
   button, badge or field looks identical on ahcut.com and app.ahcut.com.
   Tokens live in ./theme.css; rules in ./DESIGN.md. Both apps add
   `@source` for this folder so Tailwind generates these classes.
   ============================================================================ */

export type ClassValue = string | false | null | undefined;

/** Join truthy class fragments. Apps that need conflict resolution wrap this
 *  (the dashboard's `cn` adds tailwind-merge); recipes never emit conflicts. */
export function cx(...parts: ClassValue[]): string {
  return parts.filter(Boolean).join(" ");
}

/* ------------------------------------------------------------------ Button */
export type ButtonVariant = "primary" | "secondary" | "quiet" | "ghost" | "danger" | "link";
/** `icon` (44px) and `icon-sm` (36px) are square, icon-only sizes — the button
 *  then needs an accessible name (`aria-label`). */
export type ButtonSize = "sm" | "md" | "lg" | "icon" | "icon-sm";

const buttonBase =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium " +
  "no-underline transition-colors duration-150 cursor-pointer select-none " +
  "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 " +
  "[&_svg]:size-[1.125em] [&_svg]:shrink-0";

const buttonVariants: Record<ButtonVariant, string> = {
  /* the app's Export button — at most ONE per view */
  primary: "border border-cta-edge bg-cta text-cta-fg hover:bg-cta-hover active:bg-cta-active",
  /* strong neutral (header CTA, secondary actions next to a primary) */
  secondary: "border border-transparent bg-ink text-on-ink hover:bg-ink-hover",
  /* outlined, low emphasis */
  quiet: "border border-line-control bg-surface-0 text-fg-0 hover:bg-surface-1 active:bg-surface-2",
  /* borderless, toolbars and table row actions */
  ghost: "border border-transparent bg-transparent text-fg-0 hover:bg-surface-2 active:bg-surface-3",
  /* destructive — outlined so it never competes with the primary */
  danger: "border border-danger/60 bg-surface-0 text-danger hover:bg-danger-subtle",
  /* inline text action — no size classes, so it inherits the surrounding font size */
  link:
    "h-auto border-0 bg-transparent px-0 text-accent-ink underline decoration-1 underline-offset-4 " +
    "hover:text-accent-ink-strong",
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-4 text-[0.9375rem]",
  lg: "h-12 px-5 text-base",
  icon: "size-11 px-0 text-[0.9375rem]",
  "icon-sm": "size-9 px-0 text-sm",
};

export interface ButtonRecipe {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
}

export function buttonClass({ variant = "primary", size = "md", block = false }: ButtonRecipe = {}): string {
  return cx(buttonBase, buttonVariants[variant], variant !== "link" && buttonSizes[size], block && "w-full");
}

/* ------------------------------------------------------------------- Badge */
export type BadgeTone = "neutral" | "accent" | "ok" | "warn" | "danger";

const badgeBase =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm border px-2 py-0.5 " +
  "text-xs font-medium leading-5";

/** Tone borders are alpha-composited over their own fill, so the dark set needs
 *  more of the tone than the light one to stay a visible boundary: at /35 on
 *  dark they measure 1.88–2.37:1, under the 3:1 floor. The dark alphas below
 *  are measured over their own ground (ok 4.04, warn 4.30, danger 3.44, accent
 *  4.43, neutral 3.64) — DESIGN.md §3. */
const badgeTones: Record<BadgeTone, string> = {
  neutral: "border-line-strong dark:border-line-control bg-surface-1 text-fg-1",
  accent: "border-accent-ink/35 dark:border-accent-ink/60 bg-surface-1 text-accent-ink",
  ok: "border-ok/35 dark:border-ok/60 bg-ok-subtle text-ok",
  warn: "border-warn/35 dark:border-warn/60 bg-warn-subtle text-warn",
  danger: "border-danger/35 dark:border-danger/70 bg-danger-subtle text-danger",
};

export function badgeClass(tone: BadgeTone = "neutral"): string {
  return cx(badgeBase, badgeTones[tone]);
}

/** Small round status marker placed BEFORE a text label (never alone). */
export function statusDotClass(tone: BadgeTone = "neutral"): string {
  const fill: Record<BadgeTone, string> = {
    neutral: "bg-fg-2",
    accent: "bg-accent-ink",
    ok: "bg-ok",
    warn: "bg-warn",
    danger: "bg-danger",
  };
  return cx("inline-block size-1.5 shrink-0 rounded-full", fill[tone]);
}

/* ---------------------------------------------------------- Callout / Alert */
export type CalloutTone = "neutral" | "ok" | "warn" | "danger";

/** Same composited-border rule as the badge tones above; the neutral tone takes
 *  the same `line-control` boundary as the neutral badge (3.90 on surface-1),
 *  because on dark `line-strong` is 1.35 there and `line` lower still — a
 *  neutral notice on a dark page would otherwise have no edge at all, its fill
 *  being one 1.06:1 step off the page ground. */
const calloutTones: Record<CalloutTone, string> = {
  neutral: "border-line dark:border-line-control bg-surface-1",
  ok: "border-ok/35 dark:border-ok/60 bg-ok-subtle",
  warn: "border-warn/35 dark:border-warn/60 bg-warn-subtle",
  danger: "border-danger/35 dark:border-danger/70 bg-danger-subtle",
};

const calloutIconTones: Record<CalloutTone, string> = {
  neutral: "text-fg-2",
  ok: "text-ok",
  warn: "text-warn",
  danger: "text-danger",
};

/** Container for an inline notice (web Callout, dashboard Alert). Body text
 *  stays fg-1 on every tone; the tone lives in the border, tint and icon. */
export function calloutClass(tone: CalloutTone = "neutral"): string {
  return cx("flex gap-3 rounded-lg border p-4 text-sm text-fg-1", calloutTones[tone]);
}

/** Leading glyph inside a callout (ui/glyphs.ts; decorative: pair with a visible title). */
export function calloutIconClass(tone: CalloutTone = "neutral"): string {
  return cx("mt-0.5 size-4.5 shrink-0", calloutIconTones[tone]);
}

export const calloutTitleClass = "font-medium text-fg-0";

/* ------------------------------------------------------------------ Fields */
export const fieldLabelClass = "block text-sm font-medium text-fg-0";
export const fieldHintClass = "text-sm text-fg-2";
export const fieldErrorClass = "text-sm text-danger";

const controlBase =
  "block w-full rounded-md border border-line-control bg-surface-0 text-fg-0 " +
  "transition-colors duration-150 placeholder:text-fg-2 hover:border-fg-2 " +
  "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus " +
  "disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger";

export function inputClass({ size = "md" }: { size?: "sm" | "md" } = {}): string {
  return cx(controlBase, size === "sm" ? "h-9 px-2.5 text-sm" : "h-11 px-3 text-[0.9375rem]");
}

/** Native select without the platform arrow — always render `selectIconClass`
 *  (the `chevronDown` glyph) inside a `relative` wrapper next to it. */
export function selectClass({ size = "md" }: { size?: "sm" | "md" } = {}): string {
  return cx(controlBase, "appearance-none bg-no-repeat pr-9", size === "sm" ? "h-9 pl-2.5 text-sm" : "h-11 pl-3 text-[0.9375rem]");
}

/** The chevron that replaces the native select arrow (decorative, `aria-hidden`). */
export const selectIconClass = "pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-fg-2";

export const textareaClass = cx(controlBase, "min-h-24 px-3 py-2.5 text-[0.9375rem] leading-6");

export const checkboxClass =
  "size-4 shrink-0 rounded-sm border border-line-control accent-[var(--ah-accent-ink)] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/* ---------------------------------------------------------------- Skeleton */
/** Loading placeholder block (dashboard Skeleton, web loading states). The pulse
 *  is loading feedback — removed under reduced motion (DESIGN.md §8). */
export const skeletonClass = "rounded-md bg-surface-3 motion-safe:animate-pulse";

/* ------------------------------------------------------------------- Panel */
/** Bordered surface for app screens (dashboard cards, dialogs body).
 *  Marketing pages prefer hairline sections over boxes (DESIGN.md §5). */
export function panelClass({ padded = true, tone = "default" }: { padded?: boolean; tone?: "default" | "subtle" | "danger" } = {}): string {
  const tones = {
    default: "border-line bg-surface-0",
    subtle: "border-line bg-surface-1",
    danger: "border-danger/40 bg-surface-0",
  } as const;
  return cx("rounded-lg border", tones[tone], padded && "p-5 sm:p-6");
}

/* -------------------------------------------------------------------- Link */
export const textLinkClass =
  "text-accent-ink underline decoration-accent-ink/40 decoration-1 underline-offset-[3px] " +
  "transition-colors duration-150 hover:text-accent-ink-strong hover:decoration-current";

/* ------------------------------------------------------------------- Table */
export const tableClass = "w-full border-collapse text-left text-sm";
export const tableHeadCellClass = "h-10 border-b border-line-strong px-3 text-left align-middle text-sm font-medium text-fg-2 whitespace-nowrap";
export const tableCellClass = "border-b border-line px-3 py-3 text-left align-middle text-fg-1";
export const tableRowHoverClass = "transition-colors duration-150 hover:bg-surface-1";

/* ---------------------------------------------------------------- Typography */
export const displayClass = "font-display text-display text-fg-0";
export const titleClass = "font-display text-title text-fg-0";
export const headingClass = "font-display text-heading text-fg-0";
export const leadClass = "text-lead text-fg-1";
/** Data: prices, versions, counts, ids, timestamps. */
export const dataClass = "font-mono tabular-nums";
